import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, AlertTriangle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-primary-red hover:text-primary-red-dark mb-8 transition-colors"
        >
          <ArrowLeft className="mr-2" size={20} />
          Retour à l'accueil
        </Link>

        <article className="prose prose-lg max-w-none">
          <div className="flex items-center space-x-3 mb-6">
            <Heart className="text-primary-red" size={32} />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-0">
              Jeu responsable
            </h1>
          </div>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              Chez ParisPari Hub, nous croyons que les paris en ligne doivent rester un divertissement 
              sûr et agréable. Cette page vous informe sur le jeu responsable et vous fournit des ressources 
              pour vous aider à maintenir un comportement de jeu sain.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mt-8">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={24} />
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Important</p>
                  <p className="text-gray-700">
                    Les paris en ligne sont interdits aux mineurs. Vous devez avoir au moins 18 ans 
                    pour utiliser les services de paris en ligne en France.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Qu'est-ce que le jeu responsable ?
            </h2>

            <p>
              Le jeu responsable consiste à parier de manière contrôlée et consciente, en gardant toujours 
              à l'esprit que les paris sont une forme de divertissement, pas un moyen de gagner de l'argent 
              ou de résoudre des problèmes financiers. Cela signifie :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Ne jamais parier plus que ce que vous pouvez vous permettre de perdre</li>
              <li>Fixer des limites de temps et d'argent avant de commencer</li>
              <li>Ne jamais chercher à récupérer vos pertes en augmentant vos mises</li>
              <li>Ne pas parier sous l'influence de l'alcool ou de drogues</li>
              <li>Ne pas parier quand vous êtes stressé, déprimé ou en colère</li>
              <li>Prendre des pauses régulières</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Signes d'un jeu problématique
            </h2>

            <p>
              Il est important de reconnaître les signes d'un comportement de jeu problématique. Si vous 
              vous reconnaissez dans plusieurs de ces situations, il est peut-être temps de demander de 
              l'aide :
            </p>

            <div className="bg-red-50 rounded-lg p-6 mt-4">
              <ul className="list-disc list-inside space-y-2">
                <li>Vous pensez constamment aux paris, même quand vous ne pariez pas</li>
                <li>Vous pariez des montants de plus en plus importants pour ressentir la même excitation</li>
                <li>Vous avez essayé de réduire ou d'arrêter de parier sans y parvenir</li>
                <li>Vous vous sentez agité ou irritable quand vous ne pouvez pas parier</li>
                <li>Vous pariez pour échapper aux problèmes ou soulager des sentiments désagréables</li>
                <li>Vous avez menti à votre famille ou à vos amis sur l'ampleur de vos paris</li>
                <li>Vous avez mis en danger ou perdu une relation, un emploi ou une opportunité à cause des paris</li>
                <li>Vous comptez sur les autres pour vous sortir de situations financières désespérées causées par les paris</li>
                <li>Vous pariez jusqu'à ce que vous n'ayez plus d'argent, puis vous cherchez à emprunter</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Outils de contrôle disponibles
            </h2>

            <p>
              Tous les sites de paris agréés en France sont tenus de proposer des outils de contrôle 
              pour vous aider à gérer votre activité de jeu. Ces outils sont gratuits et faciles à utiliser :
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Limites de dépôt
            </h3>
            <p>
              Vous pouvez fixer des limites sur le montant que vous pouvez déposer sur votre compte 
              de paris. Ces limites peuvent être quotidiennes, hebdomadaires ou mensuelles. Une fois 
              la limite atteinte, vous ne pourrez plus déposer d'argent jusqu'à la période suivante.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Limites de mise
            </h3>
            <p>
              Vous pouvez également fixer des limites sur le montant que vous pouvez miser par pari, 
              par jour, par semaine ou par mois. Ces limites vous aident à contrôler vos dépenses et 
              à rester dans votre budget.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Limites de temps
            </h3>
            <p>
              Certains sites proposent des limites de temps de jeu. Vous pouvez fixer une durée maximale 
              de session, après laquelle vous serez automatiquement déconnecté.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Auto-exclusion
            </h3>
            <p>
              L'auto-exclusion vous permet de vous exclure temporairement ou définitivement d'un site 
              de paris. Pendant la période d'exclusion, vous ne pourrez pas accéder à votre compte 
              ou créer un nouveau compte. Cette mesure est irréversible pendant la période choisie.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Rappels de temps
            </h3>
            <p>
              Vous pouvez activer des rappels qui vous alertent après une certaine durée de jeu. Ces 
              rappels vous aident à prendre conscience du temps passé et à faire des pauses régulières.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Conseils pour un jeu responsable
            </h2>

            <div className="bg-green-50 rounded-lg p-6 mt-4">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Bonnes pratiques</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Fixez un budget :</strong> Déterminez à l'avance combien vous pouvez vous permettre de perdre et respectez cette limite.</li>
                <li><strong>Ne pariez jamais avec de l'argent que vous ne pouvez pas perdre :</strong> Ne pariez pas avec de l'argent destiné aux factures, au loyer ou aux dépenses essentielles.</li>
                <li><strong>Traitez les paris comme un divertissement :</strong> Considérez l'argent que vous misez comme le coût d'un divertissement, pas comme un investissement.</li>
                <li><strong>Prenez des pauses :</strong> Ne pariez pas de manière continue. Prenez des pauses régulières pour vous ressourcer.</li>
                <li><strong>Ne cherchez pas à récupérer vos pertes :</strong> Les pertes font partie du jeu. Ne tentez pas de les récupérer en augmentant vos mises.</li>
                <li><strong>Ne pariez pas sous l'influence :</strong> L'alcool et les drogues altèrent votre jugement et peuvent vous amener à prendre de mauvaises décisions.</li>
                <li><strong>Équilibrez votre vie :</strong> Assurez-vous que les paris ne prennent pas le pas sur d'autres aspects importants de votre vie.</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Où trouver de l'aide
            </h2>

            <p>
              Si vous pensez avoir un problème de jeu, de l'aide est disponible. N'hésitez pas à contacter 
              ces organisations :
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mt-4 space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Joueurs Info Service</h3>
                <p className="text-gray-700 mb-2">
                  Service d'information et d'aide pour les joueurs en difficulté.
                </p>
                <p className="text-gray-700">
                  Site web : <a href="https://www.joueurs-info-service.fr" target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">www.joueurs-info-service.fr</a>
                </p>
                <p className="text-gray-700">
                  Téléphone : 09 74 75 13 13 (appel gratuit depuis la France)
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">SOS Joueurs</h3>
                <p className="text-gray-700 mb-2">
                  Association d'aide aux joueurs en difficulté et à leurs proches.
                </p>
                <p className="text-gray-700">
                  Site web : <a href="https://www.sosjoueurs.org" target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">www.sosjoueurs.org</a>
                </p>
                <p className="text-gray-700">
                  Téléphone : 01 41 83 13 13
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Évalujeu</h3>
                <p className="text-gray-700 mb-2">
                  Outil d'auto-évaluation pour identifier un éventuel problème de jeu.
                </p>
                <p className="text-gray-700">
                  Site web : <a href="https://www.evalujeu.fr" target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">www.evalujeu.fr</a>
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Notre engagement
            </h2>

            <p>
              Chez ParisPari Hub, nous nous engageons à promouvoir le jeu responsable. Nous :
            </p>

            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fournissons des informations claires sur les risques associés aux paris</li>
              <li>Encourageons l'utilisation des outils de contrôle disponibles</li>
              <li>Mettons en avant les ressources d'aide disponibles</li>
              <li>Ne ciblons jamais les mineurs ou les personnes vulnérables</li>
              <li>Promouvons un message de jeu responsable dans tous nos contenus</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
              Contact
            </h2>

            <p>
              Si vous avez des questions sur le jeu responsable ou si vous souhaitez nous signaler un 
              problème, contactez-nous :
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mt-4">
              <p className="font-semibold text-gray-900">ParisPari Hub</p>
              <p className="text-gray-700">Email : <a href="mailto:info@parisparihub.com" className="text-primary-red hover:underline">info@parisparihub.com</a></p>
              <p className="text-gray-700">Téléphone : <a href="tel:+33412407011" className="text-primary-red hover:underline">+33 4 12 40 70 11</a></p>
              <p className="text-gray-700">Adresse : Paris, France</p>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGaming;

